import React, { useState } from 'react';
import { ToastContainer } from 'react-toastify';
import SalesDetailPage from './SalesDetailPage';
import {
  TOP_DONORS_COLUMNS,
  SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
} from '../constants/topDonorsColumnsConfig';
import { currencyFormatter, compactCurrency } from '../utils/reportingUtils';
import { toastSuccess, toastError } from '../../../shared/toastHelper';
import GivingConfirmModal from '../../shared/components/GivingConfirmModal';

const axios = require('axios').default;

const buildFilterGroups = (venueOwnerships) => {
  const groups = [];

  if (venueOwnerships && venueOwnerships.length > 0) {
    groups.push({
      label: 'Venues',
      filters: [
        {
          id: 'venue_ownership_id',
          label: 'Venue',
          type: 'checkbox',
          options: venueOwnerships.map((vo) => ({
            value: String(vo.id),
            label: vo.name || vo.venue_name || `Venue ${vo.id}`,
          })),
        },
      ],
    });
  }

  groups.push({
    label: 'Giving',
    filters: [
      { id: 'total_donated', label: 'Total Donated', type: 'range' },
      { id: 'gift_count', label: 'Number of Gifts', type: 'range' },
    ],
  });

  return groups;
};

const rangeParams = (params, filterValue, key) => {
  if (!filterValue) return;
  if (filterValue.min != null && filterValue.min !== '') params[`${key}_min`] = filterValue.min;
  if (filterValue.max != null && filterValue.max !== '') params[`${key}_max`] = filterValue.max;
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
    params.venue_ownership_id = activeFilters.venue_ownership_id;
  }
  rangeParams(params, activeFilters.total_donated, 'total_donated');
  rangeParams(params, activeFilters.gift_count, 'gift_count');
  return params;
};

const formatSummaryDate = (dateStr) => {
  const [year, month, day] = String(dateStr).split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const GenerateSummaryButton = ({ csrfToken, dateRange }) => {
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const missingRange = !dateRange?.start || !dateRange?.end;

  const handleConfirm = () => {
    setSubmitting(true);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.post('/donation_summary_exports', {
      donation_summary_export: { start_date: dateRange.start, end_date: dateRange.end },
    })
      .then(() => {
        setConfirmOpen(false);
        toastSuccess("Donation statement generation started — you'll receive an email when it's ready to download");
      })
      .catch((err) => toastError(err.response?.data?.error || 'Could not generate donation statements, something went wrong'))
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-primary btn-sm text-nowrap"
        style={{ marginLeft: 'auto' }}
        disabled={missingRange || submitting}
        title={missingRange ? 'Select a bounded date range to generate summaries' : undefined}
        onClick={() => setConfirmOpen(true)}
      >
        Generate Donation Statements
      </button>
      <GivingConfirmModal
        open={confirmOpen}
        title="Generate Donation Statements"
        body={missingRange ? '' : `This will generate a donation summary statement for every donor with attributed gifts from ${formatSummaryDate(dateRange.start)} to ${formatSummaryDate(dateRange.end)}. Anonymous gifts are excluded. You'll receive an email with a download link when the statements are ready.`}
        confirmLabel="Generate Statements"
        pendingLabel="Generating..."
        pending={submitting}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

const YEAR_END_SUMMARY_CONFIG = {
  pageTitle: 'Year-End Summaries',
  pageSubtitle: 'Generate a donation summary for every donor in the selected date range, ready to mail. Totals cover attributed donors only and exclude anonymous gifts.',
  sidebarKey: 'donations_year_end_summary',
  defaultPreset: 'this_year',
  // Single option => the "View by" toggle is hidden; donations are ranked by order date.
  dateModeOptions: [{ key: 'order_date', label: 'Order Date' }],

  columns: TOP_DONORS_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingDonationsYearEndSummaryColumns_${teamId}`,
  firstColumnId: 'donor_name',
  defaultSortRules: [{ field: 'total_donated', direction: 'desc' }],

  kpiFromDetailResponse: true,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/donations/year-end-summary/data.json`,
  detailResponseKey: 'donors',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/donations/year-end-summary/export`,
  detailErrorMessage: 'Failed to load donor data.',
  detailTableTitle: 'Donors',
  emptyMessage: 'No donations in this period.',
  searchPlaceholder: 'Search donors...',
  paginationLabel: 'donors',
  itemLabel: (count) => `donor${count !== 1 ? 's' : ''}`,

  buildFilterGroups,
  buildDetailApiFilters,
  filterTitle: 'Filter Donors',
  filterSubtitle: 'Apply filters to narrow donor data.',

  renderToolbarExtras: ({ csrfToken, dateRange }) => (
    <GenerateSummaryButton csrfToken={csrfToken} dateRange={dateRange} />
  ),

  buildKpiCards: (current, prior) => [
    { label: 'Total Raised', value: compactCurrency(current.total_raised || 0), current: current.total_raised || 0, prior: prior.total_raised || 0 },
    { label: 'Unique Donors', value: (current.donor_count || 0).toLocaleString(), current: current.donor_count || 0, prior: prior.donor_count || 0 },
    { label: 'Total Gifts', value: (current.gift_count || 0).toLocaleString(), current: current.gift_count || 0, prior: prior.gift_count || 0 },
    { label: 'Average Gift', value: currencyFormatter.format(current.avg_gift || 0), current: current.avg_gift || 0, prior: prior.avg_gift || 0 },
  ],
  kpiGridColumns: 4,

  renderCellValue: (col, row, { currencyFormatter: cf, formatEventDate, team }) => {
    switch (col.id) {
      case 'donor_name': {
        const name = row.donor_name || '(No name)';
        if (team?.package?.crm && row.id) {
          return (
            <a href={`/teams/${team.id}/crm/fans/${row.id}`} style={{ color: 'var(--sl-fg-brand, #1982c4)' }}>
              {name}
            </a>
          );
        }
        return name;
      }
      case 'email': return row.email || '—';
      case 'total_donated': return cf.format(row.total_donated || 0);
      case 'gift_count': return (row.gift_count || 0).toLocaleString();
      case 'avg_gift': return cf.format(row.avg_gift || 0);
      case 'last_gift_at': return <span className="text-nowrap">{row.last_gift_at ? formatEventDate(row.last_gift_at) : '—'}</span>;
      default: return '—';
    }
  },
};

function ReportingDonationsYearEndSummaryPage(props) {
  return (
    <>
      <ToastContainer />
      <SalesDetailPage {...props} config={YEAR_END_SUMMARY_CONFIG} />
    </>
  );
}

export default ReportingDonationsYearEndSummaryPage;
